import { Datum } from "../../../components/docs/Datum.js";
import { note, p, table, ul } from "../blocks.js";
import { QUOTED } from "../thresholds.js";
import type { WikiTopic } from "../types.js";

export const technical: WikiTopic = {
  slug: "daily-technicals",
  title: "Daily technicals",
  summary: "52-week extremes on volume, and moves onto the trade-to-trade series.",
  group: "strategies",
  horizon: "position",
  strategyKey: "technical",
  sections: [
    {
      heading: "It runs on adjusted bars",
      blocks: [
        p(
          <>
            Adjusted is the whole point. On raw bars a 1:10 split reads as a <Datum>90%</Datum>{" "}
            crash, and the strategy fires a 52-week low on every split in the market. Corporate
            actions are the single largest source of fake extremes on Indian equities, and
            adjustment is the only thing that removes them.
          </>,
        ),
        note(
          <>
            The daily bhavcopy arrives raw and Kite's history arrives adjusted, so the bars this
            strategy reads are not interchangeable with the ones a raw-price factor reads.
          </>,
        ),
      ],
    },
    {
      heading: "Trade-to-trade",
      code: "series_to_trade_to_trade",
      blocks: [
        p(
          <>
            The exchange prints the series on the bar, so a stock moving into trade-to-trade
            surveillance is a bar-level change: yesterday's series differs from today's, and today's
            is <Datum>BE</Datum>. That means no intraday netting and mandatory delivery, which
            changes how the name can be traded at all.
          </>,
        ),
        p(
          <>
            This is the one type hardcoded to <Datum>urgent</Datum>. It skips the threshold tables
            entirely, because a surveillance move is the exchange saying something is wrong rather
            than a measurement anyone has to calibrate.
          </>,
        ),
        note(
          <>
            It needs two bars and nothing more. The 52-week window and the volume gates below do not
            apply to it, so a stock with three months of history can still produce one.
          </>,
        ),
      ],
    },
    {
      heading: "52-week extremes on volume",
      code: "high_52w_on_volume / low_52w_on_volume",
      blocks: [
        p(
          <>
            Three gates, all of which have to pass. A symbol with a shorter history than the window
            produces nothing, which is why a recent listing is silent here for its first year.
          </>,
        ),
        table(
          "52-week extreme gates",
          ["Gate", "Value"],
          [
            ["Window", <Datum key="w">{QUOTED.TECHNICAL_52W_WINDOW} sessions</Datum>],
            [
              "Volume baseline",
              <Datum key="b">median of the prior {QUOTED.TECHNICAL_VOLUME_WINDOW} sessions</Datum>,
            ],
            [
              "Volume multiple",
              <Datum key="m">{QUOTED.VOLUME_MULTIPLE_FLOOR}× the baseline</Datum>,
            ],
          ],
        ),
        p(
          <>
            The baseline is a median, and it excludes today. A mean would be wrecked for two months
            by one block deal: a single day an order of magnitude above normal drags the average up
            far enough to suppress every real spike behind it. Excluding today keeps the spike being
            measured out of the thing it is measured against.
          </>,
        ),
        p(
          <>
            Below <Datum>{QUOTED.VOLUME_MULTIPLE_FLOOR}×</Datum> a new high is a new high and
            nothing more. The volume gate is what separates a level being taken out from a price
            drifting past it.
          </>,
        ),
      ],
    },
    {
      heading: "The two extremes are exclusive",
      blocks: [
        ul([
          <>
            A close above every prior high in the window is <Datum>high_52w_on_volume</Datum>.
          </>,
          <>
            A close below every prior low in the window is <Datum>low_52w_on_volume</Datum>.
          </>,
          <>Anything between the two produces neither.</>,
        ]),
        p(
          <>
            The comparison is today's close against the window excluding today, so touching the
            level intraday and closing back inside it does not count. The payload carries the close,
            the prior extreme it broke, and the volume multiple.
          </>,
        ),
      ],
    },
    {
      heading: "Severity",
      blocks: [
        p(
          <>
            A 52-week extreme on more than <Datum>{QUOTED.TECHNICAL_URGENT_VOLUME_MULTIPLE}×</Datum>{" "}
            median volume resolves to <Datum>urgent</Datum>; anything else that clears the{" "}
            <Datum>{QUOTED.VOLUME_MULTIPLE_FLOOR}×</Datum> floor resolves to <Datum>warn</Datum>.
            Both clear the default alert floor, which makes these among the few types that reach
            Telegram at all.
          </>,
        ),
        note(
          <>
            The hourly and swing threshold tables are empty, so their types resolve to{" "}
            <Datum>info</Datum> and reach nobody. The technical types are the exception because
            their magnitude is a volume multiple with a meaning that holds across every symbol.
          </>,
        ),
        p(
          <>
            Rows are stamped at the <Datum>15:30</Datum> IST close. The job that writes them runs at{" "}
            <Datum>20:15</Datum>, after the day's scoring.
          </>,
        ),
      ],
    },
  ],
};
